<script setup lang="ts">
import { computed, useAttrs } from 'vue';

/**
 * アイコンを描画する際に利用できるタグ名。
 */
type IconTag = keyof HTMLElementTagNameMap;

/**
 * サポートするMaterial Iconsのバリアント。
 */
type IconVariant = 'filled' | 'outlined' | 'round';

const props = withDefaults(
  defineProps<{
    icon: string;
    tag?: IconTag;
    variant?: IconVariant;
    ariaLabel?: string;
  }>(),
  {
    tag: 'span' as IconTag,
    variant: 'filled' as IconVariant,
    ariaLabel: '',
  }
);

defineOptions({ inheritAttrs: false });

const attrs = useAttrs();

const variantClasses = computed(() => {
  switch (props.variant) {
    case 'outlined':
      return ['material-icons', 'material-icons-outlined'];
    case 'round':
      return ['material-icons', 'material-icons-round'];
    default:
      return ['material-icons'];
  }
});

const iconText = computed(() => props.icon.trim());

const mergedAttrs = computed(() => {
  const recordAttrs = attrs as Record<string, unknown>;
  const { class: classAttr, ...rest } = recordAttrs;
  const attrLabel = typeof recordAttrs['aria-label'] === 'string' ? recordAttrs['aria-label'].trim() : '';
  const hasExternalLabel = attrLabel.length > 0;

  const baseA11y = props.ariaLabel
    ? { role: 'img', 'aria-label': props.ariaLabel }
    : hasExternalLabel
    ? { role: 'img' }
    : { 'aria-hidden': 'true', role: 'presentation' };

  const additionalClass = Array.isArray(classAttr)
    ? classAttr
    : typeof classAttr === 'string' && classAttr.trim().length > 0
    ? classAttr.split(/\s+/).filter(Boolean)
    : [];

  const finalClass = [...variantClasses.value, ...additionalClass];

  return {
    ...baseA11y,
    ...rest,
    ...(props.ariaLabel ? { 'aria-label': props.ariaLabel } : {}),
    class: finalClass.length === 0 ? undefined : finalClass,
  };
});
</script>

<template>
  <component :is="tag" v-bind="mergedAttrs">{{ iconText }}</component>
</template>
